"use client"

import Image from "next/image"
import Link from "next/link"

import { useLocale } from "@/lib/locale-context"
import { Reveal } from "@/components/reveal"

/**
 * The name is reversed out of a solid black band, and everything a
 * reviewer triages on sits inside that same band: what he does, the
 * claim, the statement, the two actions, and the facts.
 */
export function SectionHero() {
  const { t } = useLocale()

  return (
    <section id="home" aria-label={t.nav.home} className="bg-foreground text-background">
      <div className="shell py-10 md:py-16">
        <Reveal atLoad>
          <p className="reveal t-small flex items-center gap-2.5 text-background/70">
            <span className="inline-block h-2 w-2 bg-accent" aria-hidden="true" />
            {t.hero.available}
          </p>
          <h1 className="reveal-clip t-display mt-4 uppercase md:mt-6">
            Luís
            <br className="lg:hidden" /> Peixoto
          </h1>
        </Reveal>

        <Reveal atLoad className="grid12 mt-8 md:mt-12">
          <span
            className="reveal-rule col-span-12 mb-6 block h-px bg-background/40"
            aria-hidden="true"
          />

          <div className="col-span-12 lg:col-span-6">
            <div className="reveal flex items-start gap-4 md:gap-5">
              <Image
                src="/portrait.jpg"
                alt={t.hero.name}
                width={900}
                height={1200}
                priority
                sizes="88px"
                className="objective h-[84px] w-[68px] shrink-0 object-cover md:h-[110px] md:w-[88px]"
              />
              <div className="min-w-0">
                <p className="t-small font-medium uppercase tracking-[0.06em]">
                  {t.hero.role}
                </p>
                <p className="t-lg mt-2 max-w-[22ch] text-accent">{t.hero.tagline}.</p>
              </div>
            </div>

            <div
              className="reveal mt-8 flex flex-wrap gap-3"
              style={{ ["--d" as string]: "120ms" }}
            >
              <a
                href={`mailto:${t.contact.email}`}
                className="btn t-small bg-background px-6 py-3 font-medium uppercase tracking-[0.06em] text-foreground transition-colors hover:text-background"
              >
                {t.contact.cta}
              </a>
              <Link
                href="/cv"
                className="btn t-small border border-background/60 px-6 py-3 font-medium uppercase tracking-[0.06em] text-background transition-colors hover:border-accent"
              >
                {t.hero.ctaCv}
              </Link>
            </div>
          </div>

          <div className="col-span-12 mt-10 lg:col-span-5 lg:col-start-8 lg:mt-0">
            <p
              className="reveal t-body max-w-[52ch] text-background/80"
              style={{ ["--d" as string]: "90ms" }}
            >
              {t.hero.description}
            </p>
            <dl className="reveal mt-7" style={{ ["--d" as string]: "160ms" }}>
              {t.hero.facts.map((fact) => (
                <div
                  key={fact.label}
                  className="flex justify-between gap-4 border-t border-background/25 py-2.5"
                >
                  <dt className="t-small text-background/60">{fact.label}</dt>
                  <dd className="t-small text-right">{fact.value}</dd>
                </div>
              ))}
            </dl>
          </div>
        </Reveal>
      </div>
    </section>
  )
}
